import React, { useContext, useEffect, useRef } from 'react';
import { func } from 'prop-types';
import { useParams } from 'react-router-dom';

// Components
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import Buttons from '../../components/Buttons';
import SubmitProgressHeader from '../../components/SubmitProgressHeader';

// Context
import { SequencingWizardContext } from '../../SequencingWizardContext';

// Utils
// eslint-disable-next-line import/order
import { calculateSuccessfulResults } from 'feature/loading-management/utils/calculate';
import SequencingProgress from '../../components/SequencingProgress';

const PrimaryStep6 = ({ editBatchApplySequence, close }) => {
  const {
    batchUpdating,
    data: {
      applyOnFutureDate,
      changeList,
      currentAffiliate,
      currentSelection,
      futureDate,
      inventoryOwners,
      locations,
      products,
    },
    initialData,
    progress,
    setCurrentStep,
    setData,
    setProgress,
  } = useContext(SequencingWizardContext);
  const { dataCompanyId, customerIdName } = currentAffiliate;
  const { viewBy } = useParams();
  const isMounted = useRef(true);

  const handleStartAnother = () => {
    setData(initialData);
    setCurrentStep(0);
  };

  useEffect(() => {
    return () => setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {
      action: 'Prepend',
      companyCustomerIdName: customerIdName,
      currentSelection,
      dataCompanyId,
      futureSequenceDate: futureDate,
      inventoryOwners,
      locations,
      products,
      viewBy,
    };
    editBatchApplySequence(params).then(result => {
      isMounted.current &&
        setData(prevData => ({
          ...prevData,
          changeList: result,
        }));
      isMounted.current && setProgress(100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inventoryOwner = `${inventoryOwners[0]?.companyCustomerIdName} ${inventoryOwners[0]?.nameLong}`;
  const successfulResults = calculateSuccessfulResults(changeList);

  return (
    <>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          display: 'flex',
          flexDirection: 'column',
          // height of other components is 157px
          // prevents progress bar from looking off
          height: 'calc(100% - 157px)',
          overflowY: 'auto',
          pt: 3,
        }}
      >
        <SubmitProgressHeader totalProgress={progress} />
        <Typography
          sx={{
            marginBottom: 1,
            marginTop: 4.5,
            px: 2,
            py: 0,
            textAlign: 'center',
          }}
          variant="body1"
        >
          {batchUpdating ? `${inventoryOwner} successfully set` : `Setting ${inventoryOwner}`} as
          Primary for the below products and locations.
        </Typography>
        {!batchUpdating && (
          <Typography sx={{ marginBottom: 2, px: 2, py: 0, textAlign: 'center' }} variant="body1">
            A total of{' '}
            {successfulResults === 1
              ? `${successfulResults} change was`
              : `${successfulResults} changes were`}{' '}
            made. This change can take up to 15 minutes to appear in the application.
          </Typography>
        )}
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'graphic.lighter',
            borderBottom: '0.1em solid',
            borderBottomColor: 'graphic.lighter',
            borderTop: '0.1em solid',
            borderTopColor: 'graphic.lighter',
            display: 'flex',
            minHeight: 48,
            px: 3,
            py: 0,
            width: '100%',
            zIndex: 4,
          }}
        >
          <Typography sx={{ color: 'primary.dark' }} variant="subtitle1">
            Summary
          </Typography>
        </Box>
        {applyOnFutureDate === 'yes' && (
          <>
            <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
              <Typography sx={{ color: 'text.secondary' }} variant="subtitle1">
                Apply Sequence on Future Date
              </Typography>
            </Box>
            <Box
              alignItems="center"
              bgcolor="graphic.lighter"
              display="flex"
              height={48}
              px={3}
              py={2}
            >
              <Typography variant="body1">{format(new Date(futureDate), 'M/d/yyyy')}</Typography>
            </Box>
          </>
        )}
        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography sx={{ color: 'text.secondary' }} variant="subtitle1">
            Primary Inventory Owner
          </Typography>
        </Box>
        <Box
          alignItems="center"
          display="flex"
          height={48}
          px={3}
          py={2}
          sx={{ backgroundColor: 'graphic.lighter' }}
        >
          <Typography variant="body1">{inventoryOwners[0].nameLong}</Typography>
        </Box>
        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography sx={{ color: 'text.secondary' }} variant="subtitle1">
            Updating Sequences
          </Typography>
        </Box>
        <Box height="100%" sx={{ backgroundColor: 'graphic.lighter' }}>
          <SequencingProgress />
        </Box>
      </Box>
      <Buttons
        handleLeftButtonClick={handleStartAnother}
        handleRightButtonClick={close}
        leftLabel="Start Another"
        loadingData={batchUpdating}
        rightLabel="Close"
      />
    </>
  );
};

PrimaryStep6.propTypes = {
  close: func.isRequired,
  editBatchApplySequence: func.isRequired,
};

export default PrimaryStep6;
